import { useParams, useNavigate, Link } from "react-router-dom";
import {
    Card,
    CardBody,
    CardHeader,
    Chip,
    Button,
    Skeleton,
    Image,
    Avatar,
} from "@heroui/react";
import { LuArrowLeft, LuPencil, LuCopy, LuCircleUserRound } from "react-icons/lu";
import { useState } from "react";
import { useFetchData } from "@/hooks";
import {
    PageHeader,
    ThemedText,
    ErrorState,
    EmptyState,
    ImageLightbox,
    NoImageFallback,
} from "@/components/ui";
import { useUser } from "@/context/UserContext";
import { formatDate, formatPhoneNumber, formatCurrency, copyToClipboard } from "@/utils";

const StaffDetail = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    const { hasPermission } = useUser();
    const [lightbox, setLightbox] = useState({ isOpen: false, images: [], index: 0 });

    const { data, isLoading, error, refetch } = useFetchData(
        `staff/${id}/show`,
    );

    if (error) {
        const is404 = error?.response?.status === 404;
        return (
            <div className="p-6">
                {is404 ? (
                    <EmptyState
                        title="Data Tidak Ditemukan"
                        description="Staff yang Anda cari tidak ditemukan atau sudah dihapus."
                        actionLabel="Kembali ke Daftar"
                        onAction={() => navigate("/staff")}
                    />
                ) : (
                    <ErrorState
                        title="Gagal Memuat Detail Staff"
                        message={error?.message}
                        onRetry={refetch}
                    />
                )}
            </div>
        );
    }

    const staff = data;
    const identityCard = staff?.identity_card;
    const ktpAddress = identityCard?.address;
    const address = staff?.address;

    return (
        <div className="flex h-full w-full flex-col gap-4">
            {/* Page Header */}
            <div className="flex flex-col justify-between gap-4 sm:flex-row sm:items-center">
                <PageHeader
                    title="Detail Staff"
                    breadcrumbs={[
                        { label: "Dashboard", href: "/" },
                        { label: "Master Data" },
                        { label: "Staff", href: "/staff" },
                        isLoading
                            ? { label: "..." }
                            : { label: staff?.name },
                    ]}
                />
                <div className="flex items-center gap-2">
                    <Button
                        variant="flat"
                        size="sm"
                        startContent={<LuArrowLeft />}
                        onPress={() => navigate("/staff")}
                    >
                        Kembali
                    </Button>
                    {hasPermission("staff.edit") && (
                        <Button
                            as={Link}
                            to={`/staff/${id}/edit`}
                            color="primary"
                            size="sm"
                            startContent={<LuPencil />}
                        >
                            Edit
                        </Button>
                    )}
                </div>
            </div>

            {/* Informasi Staff */}
            <Card className="border-default-200 border" shadow="none">
                <CardHeader className="px-4 pt-4 pb-0">
                    <ThemedText size="md" className="font-semibold">
                        Informasi Staff
                    </ThemedText>
                </CardHeader>
                <CardBody className="p-4">
                    {isLoading ? (
                        <div className="flex items-start gap-4">
                            <Skeleton className="h-20 w-20 shrink-0 rounded-full" />
                            <div className="grid flex-1 grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
                                {[...Array(6)].map((_, i) => (
                                    <div key={i} className="flex flex-col gap-1">
                                        <Skeleton className="h-4 w-24 rounded-lg" />
                                        <Skeleton className="h-5 w-40 rounded-lg" />
                                    </div>
                                ))}
                            </div>
                        </div>
                    ) : (
                        <div className="flex flex-col items-start gap-4 sm:flex-row">
                            <Avatar
                                src={staff?.photo_profile?.url_600}
                                className="h-20 w-20 shrink-0 cursor-pointer"
                                radius="full"
                                onClick={() =>
                                    staff?.photo_profile &&
                                    setLightbox({
                                        isOpen: true,
                                        images: [staff.photo_profile],
                                        index: 0,
                                    })
                                }
                                showFallback
                                fallback={
                                    <NoImageFallback
                                        icon={LuCircleUserRound}
                                        iconSize={36}
                                    />
                                }
                            />
                            <div className="grid flex-1 grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
                                <InfoItem label="Nama" value={staff?.name} />
                                <InfoItem
                                    label="Handphone"
                                    value={formatPhoneNumber(staff?.handphone)}
                                />
                                <InfoItem
                                    label="WhatsApp"
                                    value={formatPhoneNumber(staff?.whatsapp)}
                                />
                                {staff?.user && (
                                    <InfoItem
                                        label="Email (Akun)"
                                        value={staff.user.email}
                                    />
                                )}
                                {staff?.user?.roles?.length > 0 && (
                                    <div className="flex flex-col gap-1">
                                        <ThemedText
                                            size="xs"
                                            variant="secondary"
                                            className="font-medium"
                                        >
                                            Role
                                        </ThemedText>
                                        <div className="flex flex-wrap gap-1">
                                            {staff.user.roles.map((role) => (
                                                <Chip
                                                    key={role.id}
                                                    size="sm"
                                                    variant="flat"
                                                >
                                                    {role.name}
                                                </Chip>
                                            ))}
                                        </div>
                                    </div>
                                )}
                            </div>
                        </div>
                    )}
                </CardBody>
            </Card>

            {/* Kepegawaian */}
            <Card className="border-default-200 border" shadow="none">
                <CardHeader className="px-4 pt-4 pb-0">
                    <ThemedText size="md" className="font-semibold">
                        Kepegawaian
                    </ThemedText>
                </CardHeader>
                <CardBody className="p-4">
                    {isLoading ? (
                        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
                            {[...Array(6)].map((_, i) => (
                                <div key={i} className="flex flex-col gap-1">
                                    <Skeleton className="h-4 w-24 rounded-lg" />
                                    <Skeleton className="h-5 w-40 rounded-lg" />
                                </div>
                            ))}
                        </div>
                    ) : (
                        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
                            <InfoItem
                                label="Jabatan"
                                value={staff?.position?.name || "-"}
                            />
                            <InfoItem
                                label="Departemen"
                                value={staff?.department?.name || "-"}
                            />
                            <div className="flex flex-col gap-1">
                                <ThemedText
                                    size="xs"
                                    variant="secondary"
                                    className="font-medium"
                                >
                                    Status Kepegawaian
                                </ThemedText>
                                <div>
                                    <Chip
                                        color={
                                            staff?.employment_status
                                                ?.badge_color || "default"
                                        }
                                        size="sm"
                                        variant="flat"
                                        className="font-medium"
                                    >
                                        {staff?.employment_status?.name ||
                                            "Unknown"}
                                    </Chip>
                                </div>
                            </div>
                            <InfoItem
                                label="Jenis Gaji"
                                value={staff?.salary_type?.name || "-"}
                            />
                            <InfoItem
                                label="Gaji"
                                value={formatCurrency(staff?.salary)}
                            />
                            <InfoItem
                                label="Tanggal Mulai"
                                value={
                                    staff?.start_date
                                        ? formatDate(staff.start_date)
                                        : "-"
                                }
                            />
                            {staff?.end_date && (
                                <InfoItem
                                    label="Tanggal Berhenti"
                                    value={formatDate(staff.end_date)}
                                />
                            )}
                        </div>
                    )}
                </CardBody>
            </Card>

            {/* KTP */}
            {(isLoading || identityCard) && (
                <Card className="border-default-200 border" shadow="none">
                    <CardHeader className="px-4 pt-4 pb-0">
                        <ThemedText size="md" className="font-semibold">
                            Data KTP
                        </ThemedText>
                    </CardHeader>
                    <CardBody className="p-4">
                        {isLoading ? (
                            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
                                {[...Array(6)].map((_, i) => (
                                    <div key={i} className="flex flex-col gap-1">
                                        <Skeleton className="h-4 w-24 rounded-lg" />
                                        <Skeleton className="h-5 w-40 rounded-lg" />
                                    </div>
                                ))}
                            </div>
                        ) : (
                            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
                                <InfoItem
                                    label="NIK"
                                    value={identityCard?.identity_number || "-"}
                                />
                                <InfoItem
                                    label="Tempat Lahir"
                                    value={identityCard?.birth_place || "-"}
                                />
                                <InfoItem
                                    label="Tanggal Lahir"
                                    value={
                                        identityCard?.birth_date
                                            ? formatDate(identityCard.birth_date)
                                            : "-"
                                    }
                                />
                                <InfoItem
                                    label="Jenis Kelamin"
                                    value={identityCard?.gender?.name || "-"}
                                />
                                <InfoItem
                                    label="Agama"
                                    value={identityCard?.religion?.name || "-"}
                                />
                                <InfoItem
                                    label="Status Pernikahan"
                                    value={identityCard?.marital_status?.name || "-"}
                                />
                                {identityCard?.image && (
                                    <div className="sm:col-span-2 lg:col-span-3">
                                        <ThemedText
                                            size="xs"
                                            variant="secondary"
                                            className="mb-2 font-medium"
                                        >
                                            Foto KTP
                                        </ThemedText>
                                        <Image
                                            src={identityCard.photo.url_600}
                                            alt="Foto KTP"
                                            className="h-32 w-auto cursor-pointer object-cover"
                                            radius="lg"
                                            onClick={() =>
                                                setLightbox({
                                                    isOpen: true,
                                                    images: [identityCard.photo],
                                                    index: 0,
                                                })
                                            }
                                        />
                                    </div>
                                )}
                            </div>
                        )}
                    </CardBody>
                </Card>
            )}

            {/* Alamat KTP */}
            {(isLoading || ktpAddress) && (
                <Card className="border-default-200 border" shadow="none">
                    <CardHeader className="px-4 pt-4 pb-0">
                        <ThemedText size="md" className="font-semibold">
                            Alamat KTP
                        </ThemedText>
                    </CardHeader>
                    <CardBody className="p-4">
                        {isLoading ? (
                            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
                                {[...Array(4)].map((_, i) => (
                                    <div key={i} className="flex flex-col gap-1">
                                        <Skeleton className="h-4 w-24 rounded-lg" />
                                        <Skeleton className="h-5 w-40 rounded-lg" />
                                    </div>
                                ))}
                            </div>
                        ) : (
                            <AddressSection address={ktpAddress} />
                        )}
                    </CardBody>
                </Card>
            )}

            {/* Alamat Domisili */}
            {(isLoading || address) && (
                <Card className="border-default-200 border" shadow="none">
                    <CardHeader className="px-4 pt-4 pb-0">
                        <ThemedText size="md" className="font-semibold">
                            Alamat Domisili
                        </ThemedText>
                    </CardHeader>
                    <CardBody className="p-4">
                        {isLoading ? (
                            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
                                {[...Array(4)].map((_, i) => (
                                    <div key={i} className="flex flex-col gap-1">
                                        <Skeleton className="h-4 w-24 rounded-lg" />
                                        <Skeleton className="h-5 w-40 rounded-lg" />
                                    </div>
                                ))}
                            </div>
                        ) : (
                            <AddressSection address={address} />
                        )}
                    </CardBody>
                </Card>
            )}

            {/* NPWP */}
            {(isLoading || staff?.tax_identification_number || staff?.npwp_image) && (
                <Card className="border-default-200 border" shadow="none">
                    <CardHeader className="px-4 pt-4 pb-0">
                        <ThemedText size="md" className="font-semibold">
                            NPWP
                        </ThemedText>
                    </CardHeader>
                    <CardBody className="p-4">
                        {isLoading ? (
                            <div className="flex flex-col gap-4">
                                <Skeleton className="h-5 w-48 rounded-lg" />
                                <Skeleton className="h-32 w-48 rounded-lg" />
                            </div>
                        ) : (
                            <div className="flex flex-col gap-4">
                                <InfoItem
                                    label="Nomor NPWP"
                                    value={staff?.tax_identification_number || "-"}
                                />
                                {staff?.npwp_image && (
                                    <div>
                                        <ThemedText
                                            size="xs"
                                            variant="secondary"
                                            className="mb-2 font-medium"
                                        >
                                            Foto NPWP
                                        </ThemedText>
                                        <Image
                                            src={staff.npwp_image.url_600}
                                            alt="Foto NPWP"
                                            className="h-32 w-auto cursor-pointer object-cover"
                                            radius="lg"
                                            onClick={() =>
                                                setLightbox({
                                                    isOpen: true,
                                                    images: [staff.npwp_image],
                                                    index: 0,
                                                })
                                            }
                                        />
                                    </div>
                                )}
                            </div>
                        )}
                    </CardBody>
                </Card>
            )}

            {/* Footer Metadata */}
            {!isLoading && staff && (
                <div className="flex flex-wrap items-center gap-x-4 gap-y-1 px-1">
                    <ThemedText size="xs" variant="secondary">
                        Dibuat:{" "}
                        {formatDate(staff.created_at, {
                            day: "2-digit",
                            month: "short",
                            year: "numeric",
                            hour: "2-digit",
                            minute: "2-digit",
                        })}
                    </ThemedText>
                    <ThemedText size="xs" variant="secondary">
                        ·
                    </ThemedText>
                    <ThemedText size="xs" variant="secondary">
                        Terakhir diubah:{" "}
                        {formatDate(staff.updated_at, {
                            day: "2-digit",
                            month: "short",
                            year: "numeric",
                            hour: "2-digit",
                            minute: "2-digit",
                        })}
                    </ThemedText>
                </div>
            )}

            {/* Image Lightbox */}
            <ImageLightbox
                isOpen={lightbox.isOpen}
                onClose={() => setLightbox({ ...lightbox, isOpen: false })}
                images={lightbox.images}
                urlKey="url_1000"
                initialIndex={lightbox.index}
            />
        </div>
    );
};

const InfoItem = ({ label, value }) => (
    <div className="flex flex-col gap-1">
        <ThemedText size="xs" variant="secondary" className="font-medium">
            {label}
        </ThemedText>
        <ThemedText size="sm">{value || "-"}</ThemedText>
    </div>
);

const AddressSection = ({ address }) => (
    <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3">
        {address?.full_address && (
            <div className="sm:col-span-2 lg:col-span-3">
                <div className="flex flex-col gap-1">
                    <ThemedText size="xs" variant="secondary" className="font-medium">
                        Alamat Lengkap
                    </ThemedText>
                    <span className="inline-flex items-center gap-1.5">
                        <ThemedText size="sm">{address.full_address}</ThemedText>
                        <LuCopy
                            size={12}
                            className="text-default-400 hover:text-default-600 shrink-0 cursor-pointer"
                            onClick={() => copyToClipboard(address.full_address, "Alamat")}
                        />
                    </span>
                </div>
            </div>
        )}
        {(address?.neighborhood_unit || address?.sub_neighborhood_unit) && (
            <InfoItem
                label="RT / RW"
                value={`${address.neighborhood_unit || "-"} / ${address.sub_neighborhood_unit || "-"}`}
            />
        )}
        {address?.village && <InfoItem label="Kelurahan" value={address.village} />}
        {address?.district && <InfoItem label="Kecamatan" value={address.district} />}
        {address?.city && <InfoItem label="Kota" value={address.city} />}
        {address?.province && <InfoItem label="Provinsi" value={address.province} />}
        {address?.postal_code && <InfoItem label="Kode Pos" value={address.postal_code} />}
        {address?.google_map_url && (
            <div className="sm:col-span-2 lg:col-span-3">
                <a
                    href={address.google_map_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-primary text-xs hover:underline"
                >
                    Lihat di Google Maps
                </a>
            </div>
        )}
    </div>
);

export default StaffDetail;
